import { createFileRoute, stripSearchParams, useNavigate, useRouter, useSearch } from '@tanstack/react-router'
import ImmersiveMedia, { type ImmersiveMediaFilter } from '@web/src/features/media/immersive-media/immersive-media'
import { env } from '@web/src/lib/env.client'
import { robotsMetaForEnvironment, seo } from '@web/src/lib/seo'
import { useMemo } from 'react'
import type { MediaSortEnum, MediaTypeEnum, NSFWRatingEnum } from 'types/graphql'
import { z } from 'zod'

const MEDIA_SORT_VALUES = ['COMMENTS', 'LATEST', 'LIKES', 'OLDEST', 'VIEWS'] as const
const MEDIA_TYPE_VALUES = ['IMAGE', 'VIDEO', 'UNDEFINED'] as const
const NSFW_RATING_VALUES = ['UNDEFINED', 'SFW', 'SUGGESTIVE', 'EROTICA', 'EXPLICIT'] as const

const isMediaSortEnum = (value: string): value is MediaSortEnum => {
  return MEDIA_SORT_VALUES.includes(value as MediaSortEnum)
}

const isMediaTypeEnum = (value: string): value is MediaTypeEnum => {
  return MEDIA_TYPE_VALUES.includes(value as MediaTypeEnum)
}

const isNsfwRatingEnum = (value: string): value is NSFWRatingEnum => {
  return NSFW_RATING_VALUES.includes(value as NSFWRatingEnum)
}

export const Route = createFileRoute('/(auth)/(fullscreen-layout)/media/$mediaId')({
  component: MediaFeedPage,
  validateSearch: z.object({
    sortBy: z.string().optional(),
    mediaType: z.string().optional(),
    nsfwRating: z.string().optional(),
    albumId: z.string().optional(),
    postId: z.string().optional(),
    disableNextPrev: z.string().optional(),
    disableCurrentUserPrivate: z.coerce.boolean().optional(),
    commentId: z.string().optional(),
  }),
  search: {
    middlewares: [
      // Removes parameters if they match default values
      stripSearchParams({
        sortBy: 'LATEST',
        mediaType: 'ALL',
        nsfwRating: 'ALL',
        albumId: '',
        postId: '',
        disableNextPrev: 'false',
        disableCurrentUserPrivate: false,
      }),
    ],
  },
  head: () =>
    seo({
      title: `Media Feed | ${env.VITE_APP_NAME}`,
      description: 'Vertical media feed',
      robots: robotsMetaForEnvironment({ all: 'noindex,nofollow' }),
      meta: [{ name: 'rating', content: 'adult' }],
    }),
})

function MediaFeedPage() {
  const { mediaId } = Route.useParams()
  const search = useSearch({ strict: false })
  const navigate = useNavigate()
  const router = useRouter()

  /** Matches prior Zod `.default(false)` when the param is omitted. */
  const disableCurrentUserPrivate = search.disableCurrentUserPrivate ?? false

  const filter: ImmersiveMediaFilter = useMemo(
    () => ({
      sortBy: search.sortBy && isMediaSortEnum(search.sortBy) ? search.sortBy : 'LATEST',
      mediaType: search.mediaType && isMediaTypeEnum(search.mediaType) ? search.mediaType : undefined,
      nsfwRating: search.nsfwRating && isNsfwRatingEnum(search.nsfwRating) ? search.nsfwRating : undefined,
      albumId: search.albumId,
      postId: search.postId,
      excludeCurrentUserPrivate: disableCurrentUserPrivate !== false,
      disableNextPrev: search.disableNextPrev === 'true',
    }),
    [
      search.sortBy,
      search.mediaType,
      search.nsfwRating,
      search.albumId,
      search.postId,
      search.disableNextPrev,
      disableCurrentUserPrivate,
    ]
  )

  const handleClose = () => {
    if (window.history.length > 1) {
      router.history.back()
      return
    }
    navigate({ to: '/gallery' } as never)
  }

  return (
    <ImmersiveMedia
      filter={filter}
      initialCommentId={typeof search.commentId === 'string' ? search.commentId : undefined}
      initialMediaId={mediaId}
      onClose={handleClose}
    />
  )
}
